import type { TagRef } from '@vizit/api';
import { RadialProgress, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { Image01Icon } from '@vizit/icons';
import { Asset, type AssetImageOverlayProps, LibraryEmptyState, thumbnailSrcSet } from '@vizit/molecules';
import type * as React from 'react';
import { AssetGrid } from '../asset-grid';
import { ImageTags } from '../image-tags';

/**
 * Where an image is: its upload still processing, its scoring under the selected category still running, done, or
 * given up on after the processing window.
 */
export type BenchmarkImageStatus = 'processing' | 'scoring' | 'ready' | 'failed';

// Placeholders have no identity of their own, so their keys are their slots in the next page.
const placeholderKeys = (count: number) => Array.from({ length: count }, (_, slot) => `next-page-slot-${slot}`);
// A view-only Asset is a plain div; the role names the placeholder for assistive tech, like the tile's own group role.
const PLACEHOLDER_ROLE = { role: 'group' } as const;

const BUSY_LABELS: Partial<Record<BenchmarkImageStatus, string>> = { processing: 'Processing…', scoring: 'Scoring…' };

export interface BenchmarkImageItem {
  /**
   * Unique identifier for the image
   */
  id: string;
  /**
   * The file name, shown above the tile
   */
  name: string;
  /**
   * Thumbnail URL; `null` until the image is uploaded
   */
  thumbnailUrl: string | null;
  largeThumbnailUrl?: string | null;
  /**
   * Full-size image to show when the thumbnail fails to load, e.g. a rendition that does not exist yet
   */
  fallbackUrl?: string | null;
  /**
   * Where the image is in processing
   */
  status: BenchmarkImageStatus;
  /**
   * The image's score under the selected product category: a number once scored, `null` when the category is scored
   * but this image is not, and left unset when no category is selected (the tile then shows no score bar)
   *
   * @optional
   */
  score?: number | null;
  /**
   * Whether the score under the selected category is still loading; the tile shows a score bar placeholder
   *
   * @optional
   * @default false
   */
  isScoreLoading?: boolean;
  heatmapOverlay?: AssetImageOverlayProps;
  tagging?: { workspaceId: string; tags: TagRef[] };
}

export interface BenchmarkImageGridProps {
  /**
   * The images to show, in display order
   */
  images: BenchmarkImageItem[];
  /**
   * Total images in the benchmark
   */
  totalCount: number;
  /**
   * Images needed before the benchmark can be scored against, used in the empty state copy
   */
  minimumImages: number;
  /**
   * Whether the images are loading
   *
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether more images can be loaded; renders the scroll sentinel that asks for them
   *
   * @optional
   * @default false
   */
  hasNextPage?: boolean;
  /**
   * Whether the next page is being loaded; shows that many placeholder tiles after the loaded ones
   *
   * @optional
   * @default false
   */
  isFetchingNextPage?: boolean;
  /**
   * How many tiles the next page brings
   *
   * @optional
   * @default 12
   */
  nextPageSize?: number;
  /**
   * Ref for the scroll sentinel below the grid, e.g. from `useInfiniteScroll`
   *
   * @optional
   */
  loadMoreRef?: (node: HTMLDivElement | null) => void;
  /**
   * Action shown in the empty state, e.g. the "Upload images" button
   *
   * @optional
   */
  emptyStateAction?: React.ReactNode;
  /**
   * Called when a processed image is pressed
   *
   * @optional
   */
  onImagePress?: (id: string) => void;
  /**
   * Additional CSS classes for the root element
   *
   * @optional
   */
  className?: string;
  columns?: number;
  selection?: {
    isSelected: (id: string) => boolean;
    isSelectionMode: boolean;
    onToggle: (id: string, options: { shiftKey: boolean }) => void;
  };
}

// Top-left is free on the benchmark page, which has no tile checkbox; the collection page may place this elsewhere.
function ProcessingBadge({ label }: { label: string }) {
  return (
    <span className="absolute top-2 left-2 z-10 flex items-center gap-2 border border-secondary bg-white px-3 py-1">
      <RadialProgress isIndeterminate size={8} aria-hidden />
      <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Secondary}>
        {label}
      </Text>
    </span>
  );
}

function BenchmarkImageTile({
  image,
  columns,
  onImagePress,
  selection,
}: {
  image: BenchmarkImageItem;
  columns?: number;
  onImagePress?: (id: string) => void;
  selection?: BenchmarkImageGridProps['selection'];
}) {
  // Each tile says what it is waiting on: an upload still processing or a scoring still running.
  const busyLabel = BUSY_LABELS[image.status];
  const isProcessing = busyLabel !== undefined;
  const isPressable = image.status === 'ready' && !!onImagePress;
  // A score bar only once a category is selected: scored tiles show the tier color, unscored ones say so.
  const hasScoreBar = image.status === 'ready' && (image.score !== undefined || !!image.isScoreLoading);
  const accessibleName = busyLabel
    ? `${image.name}, ${busyLabel.replace('…', '')}`
    : image.status === 'failed'
      ? `${image.name}, could not be processed`
      : image.name;

  const tile = (
    <Asset
      id={image.id}
      score={hasScoreBar && !image.isScoreLoading ? image.score : null}
      isUnscored={!hasScoreBar || (!image.isScoreLoading && image.score === null)}
      unscoredLabel="Unscored"
      showScoreBar={hasScoreBar}
      isViewOnly={!isPressable}
      isSelected={selection?.isSelected(image.id)}
      data-asset-id={image.id}
      onPress={isPressable ? () => onImagePress?.(image.id) : undefined}
      aria-label={accessibleName}
      {...(isPressable ? {} : { role: 'group' })}
    >
      <Asset.Label className="truncate pb-2">{image.name}</Asset.Label>
      <div className="relative">
        <Asset.Image
          name={image.name}
          src={image.thumbnailUrl}
          fallbackSrc={image.fallbackUrl}
          {...thumbnailSrcSet({ small: image.thumbnailUrl, large: image.largeThumbnailUrl }, columns)}
          isDimmed={isProcessing}
          overlay={image.heatmapOverlay}
        />
        {busyLabel && <ProcessingBadge label={busyLabel} />}
      </div>
      {selection && isPressable && (
        <Asset.Checkbox
          aria-label={`Select ${image.name}`}
          isSelected={selection.isSelected(image.id)}
          isAlwaysVisible={selection.isSelectionMode}
          onSelectedChange={(_, options) => selection.onToggle(image.id, options)}
        />
      )}
      {isProcessing && <div className="mt-2 h-4 w-full animate-pulse bg-tertiary" aria-hidden />}
      {image.status === 'failed' && (
        <Text size={TextSize.Xs} color={TextColor.Quaternary} className="pt-2">
          Couldn't process
        </Text>
      )}
    </Asset>
  );

  if (!image.tagging) return tile;

  return (
    <div>
      {tile}
      <ImageTags image={image} tags={image.tagging.tags} workspaceId={image.tagging.workspaceId} />
    </div>
  );
}

/**
 * The images of a benchmark as a grid of tiles, each captioned with its file name. Tiles still uploading or still
 * scoring are dimmed with a spinner badge saying which, and a pulsing bar where the score bar goes; each resolves on
 * its own. Processed
 * tiles open the image preview and, once a product category is selected, carry its score bar in the tier colors.
 * Without images it shows how many the benchmark needs and an upload action.
 */
export function BenchmarkImageGrid(props: BenchmarkImageGridProps) {
  const {
    images,
    totalCount,
    minimumImages,
    isLoading = false,
    hasNextPage = false,
    isFetchingNextPage = false,
    nextPageSize = 12,
    loadMoreRef,
    emptyStateAction,
    onImagePress,
    className,
    columns,
    selection,
  } = props;

  if (!isLoading && images.length === 0) {
    return (
      <LibraryEmptyState
        heading="No reference images yet."
        description={`Add at least ${minimumImages} images from your gallery, then pick a product category to score them.`}
        icon={Image01Icon}
        className={className}
      >
        {emptyStateAction}
      </LibraryEmptyState>
    );
  }

  return (
    <>
      <AssetGrid
        totalCount={totalCount}
        isLoading={isLoading}
        showCount={false}
        className={className}
        columns={columns}
      >
        {images.map((image) => (
          <BenchmarkImageTile
            key={image.id}
            image={image}
            columns={columns}
            onImagePress={onImagePress}
            selection={selection}
          />
        ))}
        {isFetchingNextPage &&
          placeholderKeys(nextPageSize).map((key) => (
            <Asset key={key} isLoading isViewOnly showScoreBar={false} aria-label="Loading image" {...PLACEHOLDER_ROLE}>
              <Asset.Image name="Loading image" src={null} />
            </Asset>
          ))}
      </AssetGrid>
      {hasNextPage && <div ref={loadMoreRef} className="h-4 w-full" aria-label="Loading more images" />}
    </>
  );
}
